import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { BookOpen, LockKey, CheckCircle, Check, ArrowRight, Sparkle, ArrowSquareOut, Storefront, Ticket } from "@phosphor-icons/react";
import Leaderboard from "@/components/Leaderboard";
import InstructorReferralCard from "@/components/InstructorReferralCard";
import InstructorEarningsCard from "@/components/InstructorEarningsCard";
import { CompletionsCard } from "@/components/CompletionsCard";
import { SchoolActivityCard } from "@/components/SchoolActivityCard";
import { LevelStreakCard } from "@/components/LevelStreakCard";
import { GemShopCard } from "@/components/GemShopCard";
import { RecapArchiveCard } from "@/components/RecapArchiveCard";
import { NdaGate } from "@/components/NdaGate";
import { useFeatures } from "@/lib/features";

import { roleLabel } from "@/lib/roles";
import { getLastGrade, setLastGrade } from "@/lib/gradePref";
import { useI18n } from "@/contexts/I18nContext";
import { compactCoins } from "@/components/HeaderBar";

export default function DashboardPage() {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const feat = useFeatures();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const [communityStatus, setCommunityStatus] = useState(null);

  useEffect(() => {
    api.get("/community/status").then(({ data }) => setCommunityStatus(data)).catch(() => setCommunityStatus(null));
  }, []);

  const loadCourses = async () => {
    try {
      const { data } = await api.get("/courses", { params: lang !== "en" ? { lang } : {} });
      setCourses(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) return;
    loadCourses();
    // eslint-disable-next-line
  }, [user, lang]);

  if (!user) return null;
  const isInstructor = ["super_admin", "level2_instructor", "level1_instructor"].includes(user.role);
  const isSuperAdmin = user.role === "super_admin";
  const isSchoolStaff = ["school_board_admin", "school_admin", "teacher"].includes(user.role);
  const displayName = user.kind === "user" ? user.name : (user.name || `Token ${user.code}`);

  const roleLabelText = user.kind === "token" ? t("Anonymous Session") : roleLabel(user.role);

  return (
    <div className="bg-grain min-h-screen overflow-x-hidden">
      <NdaGate />
      <div className="max-w-6xl mx-auto px-5 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-1 mb-8"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#000000] font-display">
            {user.kind === "token" ? "Anonymous Session" : roleLabel(user.role)}
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-display">
            {t("Hey,")} <span className={user.flair_color ? "" : "brand-text"} style={user.flair_color ? { color: user.flair_color } : undefined}>{displayName}</span>
            {user.flair_title && (
              <span data-testid="dashboard-flair-title" className="ml-2 align-middle inline-block px-2.5 py-1 rounded-full text-xs font-display font-extrabold uppercase tracking-wider text-white"
                style={{ background: user.flair_color || "#1F2937" }}>
                {user.flair_title}
              </span>
            )}
          </h1>
          <p className="text-[#000000] mt-1">
            {isSuperAdmin
              ? t("Admin home base — jump into operations or review the platform overview below.")
              : (user.completed_lesson_ids || []).length === 0
                ? t("Build your first streak today.")
                : `${t("Pick up where you left off.")} ${user.streak ? `🔥 ${t("{n}-day streak — keep it alive.").replace("{n}", user.streak)}` : t("Build your first streak today.")}`}
          </p>
        </motion.div>

        <h2 className="text-2xl font-bold mb-4 font-display flex items-center gap-2">
          <BookOpen size={26} weight="fill" color="#CCFF00" className="icon-outline" />
          {isSuperAdmin ? t("Course catalog overview") : t("Your courses")}
        </h2>

        {loading && <div className="text-[#000000]">{t("Loading...")}</div>}
        {err && <div className="text-[#FF5100]">{err}</div>}

        <div className="grid lg:grid-cols-[2fr_1fr] gap-6 min-w-0">
          <div className="grid sm:grid-cols-2 gap-5 min-w-0">
            {courses.map((c) => (
              <motion.div
                key={c.id}
                whileHover={{ y: -3 }}
                className="card card-interactive relative flex flex-col gap-3 overflow-hidden"
                onClick={() => {
                  if (!c.is_unlocked) return nav("/catalog");
                  const last = isSchoolStaff ? getLastGrade(user.id, c.id) : null;
                  nav(`/course/${c.id}${last ? `?grade=${last}` : ""}`);
                }}
                data-testid={`course-card-${c.id}`}
              >
                {c.cover_image_url && (
                  <img src={c.cover_image_url} alt="" aria-hidden
                    className="absolute inset-[5px] m-auto max-h-[calc(100%-10px)] max-w-[calc(100%-10px)] object-contain opacity-25 pointer-events-none select-none" />
                )}
                {isInstructor && (
                  <button
                    type="button"
                    title="Open course in a new window"
                    aria-label="Open course in a new window"
                    className="absolute top-3 right-3 z-10 p-2 rounded-xl bg-[#0f0f0f]/70 backdrop-blur hover:bg-[#0f0f0f]/90 border border-white/10"
                    onClick={(e) => { e.stopPropagation(); window.open(`/course/${c.id}`, "_blank", "noopener"); }}
                    data-testid={`open-course-new-window-${c.id}`}
                  >
                    <ArrowSquareOut size={18} weight="bold" color="#00E5FF" />
                  </button>
                )}
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-xl font-bold font-display leading-tight">{c.title}</h3>
                  {c.is_unlocked
                    ? <span className="w-6 h-6 rounded-full grid place-items-center shrink-0" style={{ background: "#CCFF00", border: "1.5px solid #1F2937" }} data-testid={`course-complete-badge-${c.id}`}><Check size={14} weight="bold" color="#111827" /></span>
                    : <LockKey size={22} weight="duotone" color="#52525B" />}
                </div>
                <div className={String(user.grade) === "DEMO" ? "flex flex-col items-start gap-1" : "flex items-center justify-start gap-2 flex-wrap"}>
                  {user.grade && (
                    <span data-testid={`course-card-grade-${c.id}`} className="inline-block w-fit text-xs font-display font-bold px-3 py-1 rounded-full"
                      style={{ background: "#0A0D14", color: "#CCFF00" }}>
                      {String(user.grade) === "DEMO" ? t("Demo") : `${t("Grade")} ${String(user.grade).replace("G", "")}`}
                    </span>
                  )}
                  <span className="text-xs font-bold text-black" data-testid={`course-card-lessons-${c.id}`}>
                    {c.lesson_count} {t("lessons")}
                  </span>
                </div>
                <p className="text-xs sm:text-sm font-bold text-black whitespace-nowrap overflow-hidden text-ellipsis">{c.description}</p>
                {String(user.grade) === "DEMO" && (
                  <div className="flex items-center gap-1.5 mt-1" data-testid={`course-card-lang-${c.id}`}>
                    {[["en", "English"], ["fr", "Français"]].map(([code, label]) => (
                      <button key={code} type="button" data-testid={`demo-lang-${code}`}
                        onClick={(e) => { e.stopPropagation(); if (code !== lang) setLang(code); }}
                        className="px-3 py-1 rounded-full text-xs font-display font-bold transition-all"
                        style={{
                          background: lang === code ? "#0A0D14" : "#FFFFFF",
                          color: lang === code ? "#CCFF00" : "#52525B",
                          border: `1px solid ${lang === code ? "#0A0D14" : "#C4C8CE"}`,
                          cursor: "pointer",
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                {(c.progress_percent ?? 0) >= 100 && (
                  <button
                    data-testid={`dash-print-cert-${c.id}`}
                    className="btn-primary text-sm flex items-center justify-center gap-2 mt-1"
                    onClick={(e) => { e.stopPropagation(); nav(`/certificate/${c.id}`); }}
                  >
                    <CheckCircle size={16} weight="fill" /> {t("Print your certificate")}
                  </button>
                )}
                {isSchoolStaff && (
                  <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                    <div className="text-xs text-[#000000] mb-1.5 font-semibold">{t("Take it as a grade:")}</div>
                    <div className="grid grid-cols-7 gap-1.5" data-testid={`dash-grade-tiles-${c.id}`}>
                      {["G7", "G8", "G9", "G10", "G11", "G12", "DEMO"].map((g) => {
                        const isLast = getLastGrade(user.id, c.id) === g;
                        const label = g === "DEMO" ? "Demo" : g.slice(1);
                        const noun = g === "DEMO" ? "the evaluator Demo" : `Grade ${g.slice(1)}`;
                        return (
                          <button
                            key={g}
                            data-testid={`dash-grade-tile-${c.id}-${g}`}
                            className={"rounded-lg border-2 py-1.5 font-display font-bold text-[#000000] hover:border-[#CCFF00] hover:bg-[rgba(204,255,0,0.15)] transition-colors " +
                              (g === "DEMO" ? "text-xs " : "text-sm ") +
                              (isLast ? "border-[#CCFF00] bg-[rgba(204,255,0,0.25)]" : "border-[#C4C8CE] bg-white")}
                            title={isLast ? `Your last pick — ${noun}` : `Open this course as ${noun}`}
                            onClick={() => { setLastGrade(user.id, c.id, g); nav(`/course/${c.id}?grade=${g}`); }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          {!isSuperAdmin && <Leaderboard />}
        </div>

        {user.kind === "user" && user.role === "public_student" && !loading && courses.some((c) => !c.is_unlocked) && (
          <RedeemCodeCard onRedeemed={loadCourses} />
        )}

        {/* Operations shortcut — instructors + school staff (board/school admins, teachers) */}
        {(isInstructor || isSchoolStaff) && (          <div className="card card-interactive mb-8 flex items-center justify-between" onClick={() => nav("/admin")} data-testid="dashboard-admin-shortcut">
            <div>
              <h3 className="text-xl font-bold font-display">{t("Operations Panel")}</h3>
              <p className="text-[#000000] text-sm">
                {isSchoolStaff
                  ? user.role === "teacher"
                    ? t("Manage your classes, students & roster")
                    : t("Manage your users, schools, teachers & roster")
                  : t("Manage users, tokens, courses & finances")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                title="Open Operations Panel in a new window"
                aria-label="Open Operations Panel in a new window"
                className="btn-ghost p-2 rounded-xl hover:bg-[#CCFF00]/10"
                onClick={(e) => { e.stopPropagation(); window.open("/admin", "_blank", "noopener"); }}
                data-testid="open-admin-new-window"
              >
                <ArrowSquareOut size={22} weight="bold" color="#00E5FF" />
              </button>
              <ArrowRight size={28} weight="bold" color="#CCFF00" className="icon-outline" />
            </div>
          </div>
        )}

        {/* Front Desk mode — super admin quick access to the phone till */}
        {isSuperAdmin && (
          <div className="card card-interactive mb-8 flex items-center justify-between" onClick={() => nav("/frontdesk")} data-testid="dashboard-frontdesk-shortcut">
            <div>
              <h3 className="text-xl font-bold font-display">{t("Front Desk")}</h3>
              <p className="text-[#000000] text-sm">{t("Phone till — sell items, take QR payments, check in packs")}</p>
            </div>
            <div className="flex items-center gap-3">
              <Storefront size={26} weight="duotone" color="#00E5FF" />
              <ArrowRight size={28} weight="bold" color="#CCFF00" className="icon-outline" />
            </div>
          </div>
        )}

        {/* Community — anonymous student space (hidden until launch; admins preview) */}
        {(communityStatus?.enabled || communityStatus?.admin) && (
          <div className="card card-interactive mb-8 flex items-center justify-between" onClick={() => nav("/community")} data-testid="dashboard-community-shortcut">
            <div>
              <h3 className="text-xl font-bold font-display">{t("Community")}
                {communityStatus?.admin && !communityStatus?.enabled && (
                  <span className="ml-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#F4F4F5] text-[#000000] align-middle" data-testid="community-hidden-chip">Hidden from members</span>
                )}
              </h3>
              <p className="text-[#000000] text-sm">{t("Share wins & ask questions — anonymously")}</p>
            </div>
            <span className="text-2xl" aria-hidden>→</span>
          </div>
        )}

        {/* Completions per week per school — admins only */}
        {(isSuperAdmin || ["school_board_admin", "school_admin"].includes(user.role)) && <CompletionsCard />}

        {/* Class handoff activity feed — admins only */}
        {(isSuperAdmin || ["school_board_admin", "school_admin"].includes(user.role)) && <SchoolActivityCard />}

        {/* Stats bento — hide for super_admin & school staff (they don't take courses) */}
        {!isSuperAdmin && !isSchoolStaff && (
          <>
          <LevelStreakCard />
          <GemShopCard />
          <RecapArchiveCard />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            <StatCard label={t("Coins")} value={compactCoins(user.coins_balance ?? user.xp ?? 0)} accent="#FFD600" testid="stat-xp" />
            <StatCard label={t("Streak")} value={user.streak || 0} suffix={t("days")} accent="#FF5100" testid="stat-streak" />
            <StatCard label={t("Lessons done")} value={(user.completed_lesson_ids || []).length} accent="#00E5FF" testid="stat-lessons" />
            <StatCard label={t("Quizzes passed")} value={(user.passed_quiz_ids || []).length} accent="#CCFF00" testid="stat-quizzes" />
          </div>
          </>
        )}

        {/* Instructor referral link */}
        {isInstructor && feat.instructor_features && (
          <div className="mb-8 space-y-4">
            <InstructorReferralCard />
            {!isSuperAdmin && <InstructorEarningsCard />}
          </div>
        )}
      </div>
    </div>
  );
}


function RedeemCodeCard({ onRedeemed }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/site/voucher/redeem", { code });
      toast.success(data.already ? "That code is already on your account" : "Access code accepted — course unlocked! 🎉");
      setCode("");
      onRedeemed();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={redeem} className="card mt-8 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="redeem-code-card">
      <div className="flex-1 min-w-0">
        <div className="font-bold font-display flex items-center gap-2">
          <Ticket size={18} weight="duotone" color="#84cc16" /> Have an access code?
        </div>
        <div className="text-xs text-[#000000]">Bought online training at the front desk? Enter your E650 code to unlock your course.</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <input className="input-base text-sm w-40" placeholder="E650-XXXXX" value={code}
          onChange={(e) => setCode(e.target.value)} data-testid="redeem-code-input" />
        <button type="submit" className="btn-primary text-sm" disabled={busy} data-testid="redeem-code-btn">
          {busy ? "…" : "Redeem"}
        </button>
      </div>
    </form>
  );
}

function StatCard({ label, value, suffix, accent, testid }) {
  return (
    <div className="card flex flex-col gap-1 min-w-0" data-testid={testid}>
      <span className="text-[10px] sm:text-xs uppercase tracking-wider text-[#000000] break-words leading-tight">{label}</span>
      <span className="text-2xl sm:text-3xl font-extrabold font-display glow-text" style={{ color: accent }}>
        {value}{suffix && <span className="text-base font-medium text-[#000000] ml-1">{suffix}</span>}
      </span>
    </div>
  );
}

